import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from './SEOHead';
import RelatedLinks from './seo/RelatedLinks';

const platforms = [
  {
    name: 'WordPress',
    note: 'One click plugin install or manual snippet. Works with any theme.',
    icon: <img src="/integrations/wordpress.png" alt="WordPress" className="w-7 h-7 rounded object-contain" />,
  },
  {
    name: 'Shopify',
    note: 'Native Shopify app. One click install, no code required.',
    icon: <img src="/integrations/shopify.png" alt="Shopify" className="w-7 h-7 rounded object-contain" />,
  },
  {
    name: 'Squarespace',
    note: 'Works with Ajax / Mercury page loading. No need to disable it.',
    icon: <img src="/integrations/squarespace.png" alt="Squarespace" className="w-7 h-7 rounded object-contain" />,
  },
  {
    name: 'Webflow',
    note: 'Project level custom code. Compatible with Barba.js transitions.',
    icon: <img src="/integrations/webflow.png" alt="Webflow" className="w-7 h-7 rounded object-contain" />,
  },
  {
    name: 'Wix',
    note: 'Custom Code settings. Works on both SPA and multi page templates.',
    icon: <img src="/integrations/wix.png" alt="Wix" className="w-7 h-7 rounded object-contain" />,
  },
  {
    name: 'Google Tag Manager',
    note: 'Deploy as a Custom HTML tag. Fires on all pages via trigger.',
    icon: <img src="/integrations/googletag.png" alt="Google Tag Manager" className="w-7 h-7 rounded object-contain" />,
  },
];

const Compatibility = () => {
  return (
    <>
    <SEOHead
      title="Platform Compatibility"
      description="Cayda works across every major website builder. Our script is platform-aware and adapts to Squarespace, WordPress, Shopify, Webflow, Wix, and more."
      path="/compatibility"
    />
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="border-b border-ink-faint/20 bg-cream/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="text-base font-display font-semibold tracking-tight text-ink">Cayda</Link>
          <Link to="/sign-up" className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all">Get started</Link>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-3xl mx-auto px-6 pt-16 pb-12">
        <p className="text-xs font-display font-semibold uppercase tracking-widest text-ink-faint mb-4">Platform Compatibility</p>
        <h1 className="text-3xl sm:text-4xl font-display font-bold text-ink tracking-tight leading-tight mb-5">
          One script. Every platform.
        </h1>
        <p className="text-base font-display text-ink-muted leading-relaxed max-w-xl">
          Cayda's embed code is a single line of HTML. You paste it once, and it handles the rest — regardless of whether your site runs on WordPress, Squarespace, Shopify, or a fully custom stack. Here's how that works.
        </p>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 pb-20">
        <div className="space-y-14 text-[15px] font-display text-ink-secondary leading-relaxed">

          {/* The problem */}
          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">The problem with "just paste a script"</h2>
            <p className="mb-3">
              Most tools tell you to drop a snippet into your site and call it a day. And on a standard HTML website, that works fine — every page loads fresh, the script runs, everything connects.
            </p>
            <p>
              But modern website builders don't work like that. Platforms like Squarespace, Wix, and some Shopify and Webflow setups use what's called single-page architecture. When someone clicks a link on your site, the platform doesn't load a whole new page. Instead, it swaps out the content and updates the URL in the background. It's faster for visitors, but it means any third-party script only actually runs once — on that very first page. Navigate anywhere else, and the script goes quiet.
            </p>
          </section>

          {/* What we do */}
          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">What Cayda does differently</h2>
            <p className="mb-3">
              Our script is platform-aware. It doesn't just load and hope for the best — it actively listens for the specific way each platform transitions between pages, and adapts accordingly.
            </p>
            <p>
              When your site navigates to a new page, Cayda detects the transition, cleans up notes from the previous page, rebuilds its interface if the platform replaced part of the page, and loads the correct notes for wherever your team member just landed. This happens automatically, every time, on every page.
            </p>
          </section>

          {/* Specific platforms */}
          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-4">How it works on each platform</h2>

            <div className="space-y-4">
              <div className="bg-white border border-ink-faint/20 rounded-xl p-5">
                <p className="text-sm font-display font-semibold text-ink mb-2">Squarespace</p>
                <p className="text-sm font-display text-ink-muted leading-relaxed">
                  Squarespace uses an internal system called Mercury for page transitions. When someone clicks between pages, Mercury swaps the content without reloading the browser. Most embedded scripts break here — they load on the first page and never fire again. Cayda listens for Mercury's own transition events, so it knows exactly when a page change happens and responds to it. Your notes show up on every page, not just the homepage.
                </p>
              </div>

              <div className="bg-white border border-ink-faint/20 rounded-xl p-5">
                <p className="text-sm font-display font-semibold text-ink mb-2">Webflow + page transitions</p>
                <p className="text-sm font-display text-ink-muted leading-relaxed">
                  Webflow sites that use standard navigation work instantly — each page loads fresh, and so does Cayda. But some Webflow sites use libraries like Barba.js or Swup for smooth animated transitions between pages. These libraries swap page content without a full reload. Cayda detects these transition events too, so it stays active even on heavily customized Webflow builds.
                </p>
              </div>

              <div className="bg-white border border-ink-faint/20 rounded-xl p-5">
                <p className="text-sm font-display font-semibold text-ink mb-2">Wix</p>
                <p className="text-sm font-display text-ink-muted leading-relaxed">
                  Wix templates come in two flavors: multi-page (full reloads) and single-page (content swaps). Cayda covers both. On multi-page Wix sites, it works on first load with zero configuration. On single-page templates, our polling system picks up URL changes and keeps everything in sync as visitors navigate.
                </p>
              </div>

              <div className="bg-white border border-ink-faint/20 rounded-xl p-5">
                <p className="text-sm font-display font-semibold text-ink mb-2">WordPress & Shopify</p>
                <p className="text-sm font-display text-ink-muted leading-relaxed">
                  These platforms typically do full page reloads on navigation, which means the script re-runs on every page automatically. WordPress users can install our one-click plugin or add the snippet manually. Shopify users add it to their theme layout file. In both cases, Cayda works across your entire site right out of the box.
                </p>
              </div>

              <div className="bg-white border border-ink-faint/20 rounded-xl p-5">
                <p className="text-sm font-display font-semibold text-ink mb-2">Custom & static sites</p>
                <p className="text-sm font-display text-ink-muted leading-relaxed">
                  If your site is a React app, a Next.js project, or plain HTML — the same script tag works. For single-page apps that use the browser's History API for routing, Cayda intercepts those navigation events directly. For everything else, there's a lightweight polling fallback that catches any page change within a second.
                </p>
              </div>
            </div>
          </section>

          {/* Under the hood */}
          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Three layers of detection</h2>
            <p className="mb-4">
              Rather than relying on a single approach and hoping it covers every case, Cayda uses a layered strategy. If one method doesn't apply to your platform, the next one picks it up.
            </p>

            <div className="grid sm:grid-cols-3 gap-3">
              <div className="bg-white border border-ink-faint/20 rounded-lg p-4">
                <div className="w-8 h-8 rounded-lg bg-ink text-white flex items-center justify-center text-xs font-display font-bold mb-3">1</div>
                <p className="text-sm font-display font-semibold text-ink mb-1">Platform events</p>
                <p className="text-xs font-display text-ink-faint leading-relaxed">
                  We listen for framework-specific signals — Squarespace's Mercury loader, Barba.js, Swup, Turbo, and Turbolinks. When one of these fires, we know a page just changed.
                </p>
              </div>
              <div className="bg-white border border-ink-faint/20 rounded-lg p-4">
                <div className="w-8 h-8 rounded-lg bg-ink text-white flex items-center justify-center text-xs font-display font-bold mb-3">2</div>
                <p className="text-sm font-display font-semibold text-ink mb-1">History API</p>
                <p className="text-xs font-display text-ink-faint leading-relaxed">
                  For sites using standard browser navigation APIs — like React Router or Vue Router — we intercept those calls directly, so we're notified the moment the URL updates.
                </p>
              </div>
              <div className="bg-white border border-ink-faint/20 rounded-lg p-4">
                <div className="w-8 h-8 rounded-lg bg-ink text-white flex items-center justify-center text-xs font-display font-bold mb-3">3</div>
                <p className="text-sm font-display font-semibold text-ink mb-1">Universal fallback</p>
                <p className="text-xs font-display text-ink-faint leading-relaxed">
                  A lightweight check runs every 800ms to catch anything the first two methods miss — proprietary loaders, hash-based routing, or platforms we haven't encountered yet.
                </p>
              </div>
            </div>
          </section>

          {/* Self-healing */}
          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Self-healing UI</h2>
            <p>
              Some platforms don't just swap content — they tear out and rebuild entire sections of the page. If Cayda's interface gets removed during one of these transitions, it notices within a second, rebuilds itself, and loads the right notes for the current page. Your session stays active. You don't have to re-authenticate. It just comes back.
            </p>
          </section>

          {/* Invisible to visitors */}
          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">None of this affects your visitors</h2>
            <p>
              All of this detection and rebuilding happens silently. The script is lightweight, runs only when an authenticated team member is present, and adds no overhead for regular visitors. No cookies, no tracking, no visible changes. Visitors will never know it's there.
            </p>
          </section>

          {/* Supported platforms grid */}
          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-4">Supported platforms</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {platforms.map((p) => (
                <div key={p.name} className="flex items-start gap-3.5 bg-white border border-ink-faint/20 rounded-lg p-4">
                  <div className="flex-shrink-0 mt-0.5">{p.icon}</div>
                  <div>
                    <p className="text-sm font-display font-semibold text-ink">{p.name}</p>
                    <p className="text-xs font-display text-ink-faint leading-relaxed mt-0.5">{p.note}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs font-display text-ink-faint mt-4">
              Don't see your platform? Cayda's layered detection means it likely works already. And if it doesn't, reach out — we'll make sure it does.
            </p>
          </section>

        </div>

        <div className="mt-14"><RelatedLinks current="/compatibility" /></div>

        {/* CTA */}
        <div className="mt-16 pt-12 border-t border-ink-faint/20 text-center">
          <h3 className="text-xl font-display font-bold text-ink tracking-tight mb-2">Ready to try it?</h3>
          <p className="text-sm font-display text-ink-faint mb-5">Set up takes under two minutes. No credit card required.</p>
          <Link to="/sign-up" className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all">Get started free</Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-ink-faint/20 bg-cream">
        <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between">
          <p className="text-xs font-display text-ink-faint">© {new Date().getFullYear()} Slowfast LLC</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">Privacy</Link>
            <Link to="/terms" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default Compatibility;
